/**
 * Utilidades para la importación masiva desde Excel.
 *
 * El archivo se parsea en el navegador (`xlsx`), se detecta el mapeo de
 * columnas por encabezado y se valida fila por fila antes de enviar el
 * lote al servidor (`import_*_batch_staff`).
 */

export type ImportKind = 'clients' | 'memberships';

export interface ImportFieldDef {
  key: string;
  label: string;
  required?: boolean;
  /** Encabezados alternativos aceptados para la autodetección. */
  aliases: string[];
  hint?: string;
}

export const CLIENT_FIELDS: ImportFieldDef[] = [
  { key: 'first_name', label: 'Nombres', aliases: ['nombres', 'nombre', 'name', 'first', 'first_name', 'firstname'] },
  { key: 'last_name', label: 'Apellidos', aliases: ['apellidos', 'apellido', 'last', 'last_name', 'lastname', 'surname'] },
  {
    key: 'full_name',
    label: 'Nombre completo',
    aliases: ['nombre completo', 'nombre y apellido', 'nombres y apellidos', 'full name', 'fullname', 'completo'],
    hint: 'Se usa cuando el archivo trae el nombre en una sola columna.',
  },
  { key: 'email', label: 'Email', aliases: ['email', 'correo', 'correo electronico', 'e mail', 'mail'] },
  { key: 'phone', label: 'Teléfono', required: true, aliases: ['telefono', 'phone', 'celular', 'whatsapp', 'movil', 'tel'] },
  { key: 'age', label: 'Edad', aliases: ['edad', 'age'] },
  { key: 'sex', label: 'Sexo', aliases: ['sexo', 'sex', 'genero'] },
  { key: 'status', label: 'Estado', aliases: ['estado', 'status'] },
  {
    key: 'emergency_contact',
    label: 'Contacto de emergencia',
    aliases: ['contacto de emergencia', 'emergencia', 'emergency contact', 'contacto'],
  },
  { key: 'notes', label: 'Notas', aliases: ['notas', 'notes', 'observaciones', 'observacion'] },
];

export const MEMBERSHIP_FIELDS: ImportFieldDef[] = [
  { key: 'phone', label: 'Teléfono de la clienta', required: true, aliases: ['telefono', 'phone', 'celular', 'whatsapp', 'movil'] },
  {
    key: 'plan_name',
    label: 'Plan',
    required: true,
    aliases: ['plan', 'membresia', 'plan de membresia', 'tipo', 'tipo de membresia', 'plan nombre'],
    hint: 'Debe coincidir con el nombre de un plan existente en el gimnasio.',
  },
  { key: 'start_date', label: 'Fecha de inicio', aliases: ['fecha de inicio', 'inicio', 'start', 'start date', 'desde'] },
  { key: 'end_date', label: 'Fecha de fin', aliases: ['fecha de fin', 'fin', 'end', 'end date', 'vencimiento', 'hasta'] },
  { key: 'price', label: 'Precio', aliases: ['precio', 'price', 'monto', 'valor', 'costo'] },
  { key: 'status', label: 'Estado', aliases: ['estado', 'status'] },
  { key: 'payment_method', label: 'Método de pago', aliases: ['metodo de pago', 'payment method', 'pago', 'forma de pago'] },
  { key: 'payment_status', label: 'Estado del pago', aliases: ['estado del pago', 'payment status', 'pagado'] },
  { key: 'sessions_remaining', label: 'Sesiones restantes', aliases: ['sesiones', 'sesiones restantes', 'sessions', 'sessions remaining'] },
];

export const fieldsFor = (kind: ImportKind): ImportFieldDef[] =>
  kind === 'clients' ? CLIENT_FIELDS : MEMBERSHIP_FIELDS;

/** Normaliza un encabezado para compararlo sin acentos ni signos. */
export const normalizeHeader = (value: unknown): string =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

/** Normaliza un teléfono dejando solo dígitos y un '+' inicial. */
export const normalizePhone = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const cleaned = raw.replace(/[^0-9+]/g, '');
  const withPlus = cleaned.startsWith('+')
    ? `+${cleaned.slice(1).replace(/[^0-9]/g, '')}`
    : cleaned.replace(/[^0-9]/g, '');
  return withPlus === '+' ? '' : withPlus;
};

/** Convierte un valor de celda a texto limpio. */
export const cellText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
};

/**
 * Detecta qué columna del archivo corresponde a cada campo.
 * Primero busca coincidencia exacta (para no confundir "Pecho" con
 * "Perímetro: Pecho") y luego coincidencia parcial.
 */
export const detectMapping = (
  headers: string[],
  fields: ImportFieldDef[]
): Record<string, string> => {
  const normalizedHeaders = headers.map(normalizeHeader);
  const mapping: Record<string, string> = {};
  const used = new Set<string>();

  const assign = (field: ImportFieldDef, matcher: (candidate: string, header: string) => boolean) => {
    if (mapping[field.key]) return;
    const candidates = [field.label, ...field.aliases].map(normalizeHeader).filter(Boolean);
    for (const candidate of candidates) {
      const idx = normalizedHeaders.findIndex((h, i) => !used.has(h) && h && matcher(candidate, h));
      if (idx >= 0) {
        mapping[field.key] = headers[idx];
        used.add(normalizedHeaders[idx]);
        return;
      }
    }
  };

  // 1) Coincidencia exacta.
  for (const field of fields) assign(field, (candidate, header) => header === candidate);

  // 2) Coincidencia parcial (solo si el alias tiene longitud suficiente).
  for (const field of fields) {
    assign(field, (candidate, header) => {
      if (candidate.length < 4 || header.length < 4) return false;
      return header.includes(candidate) || candidate.includes(header);
    });
  }

  return mapping;
};

export interface RowIssue {
  row: number;
  message: string;
}

/** Convierte un valor de celda a número, tolerando formatos con separadores. */
export const toNumber = (value: unknown): number | null => {
  const text = cellText(value);
  if (!text) return null;
  const cleaned = text.replace(/[^0-9.,-]/g, '');
  if (!cleaned) return null;
  const normalized = cleaned.includes(',') && cleaned.includes('.')
    ? cleaned.replace(/,/g, '')
    : cleaned.replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export interface PhoneInspection {
  phone: string;
  error?: string;
}

/**
 * Valida y normaliza un teléfono proveniente de Excel.
 * - Acepta espacios, guiones, paréntesis, puntos y un '+' inicial.
 * - Si la celda era numérica y es un entero seguro, usa el valor crudo
 *   para no depender de cómo Excel lo muestre (evita notación científica).
 * - Rechaza celdas con texto, dos números o extensiones.
 */
export const inspectPhoneCell = (value: unknown, rawValue?: unknown): PhoneInspection => {
  const text = cellText(value);

  if (!text) return { phone: '', error: 'Falta el teléfono' };

  if (/\b(ext|ext\.|extensión|extension)\b/i.test(text)) {
    return { phone: '', error: 'No se admiten extensiones en el teléfono' };
  }

  let source = text;
  if (typeof rawValue === 'number') {
    if (!Number.isFinite(rawValue) || !Number.isInteger(rawValue) || !Number.isSafeInteger(rawValue)) {
      return {
        phone: '',
        error: 'El teléfono está guardado como número y perdió precisión; formatea la columna como Texto',
      };
    }
    source = String(rawValue);
  } else if (/e\+?\d+/i.test(text)) {
    return {
      phone: '',
      error: 'El teléfono está en notación científica; formatea la columna como Texto',
    };
  }

  const withoutLabels = source
    .replace(/\b(tel|teléfono|telefono|cel|celular|whatsapp|móvil|movil)\b/gi, ' ')
    .trim();

  if (/[a-záéíóúüñ]/i.test(withoutLabels)) {
    return { phone: '', error: 'El teléfono contiene texto no válido' };
  }

  const groups = withoutLabels
    .split(/[/,;|]|\s{2,}/)
    .map((part) => part.replace(/\D/g, ''))
    .filter((digits) => digits.length >= 6);

  if (groups.length > 1) {
    return { phone: '', error: 'La celda contiene más de un teléfono' };
  }

  const phone = normalizePhone(source);
  const digits = phone.replace(/\D/g, '');

  if (digits.length < 7 || digits.length > 15) {
    return { phone: '', error: 'El teléfono debe tener entre 7 y 15 dígitos' };
  }

  return { phone };
};

/** Interpreta fechas en formatos dd/mm/aaaa, aaaa-mm-dd oaaaa/mm/dd. */
export const parseDate = (value: unknown): string | null => {
  const text = cellText(value);
  if (!text) return null;

  const dmy = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (year < 100) year += 2000;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const ymd = text.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return null;
};

export interface ParsedRow {
  /** Número de fila en el archivo (1 = primera fila de datos). */
  index: number;
  values: Record<string, any>;
}

export interface ParseResult {
  rows: ParsedRow[];
  issues: RowIssue[];
  validCount: number;
}

/**
 * Convierte las filas crudas del archivo en filas normalizadas y validadas.
 * Las filas con errores se conservan (para mostrarlas) pero se marcan.
 */
export const parseRows = (
  rawRows: Record<string, unknown>[],
  mapping: Record<string, string>,
  fields: ImportFieldDef[],
  kind: ImportKind,
  rawTypedRows?: Record<string, unknown>[]
): ParseResult => {
  const issues: RowIssue[] = [];
  const rows: ParsedRow[] = [];
  let validCount = 0;

  rawRows.forEach((raw, index) => {
    const values: Record<string, any> = {};
    let rowOk = true;
    let rowError = '';

    const fail = (message: string) => {
      rowOk = false;
      if (!rowError) rowError = message;
    };

    for (const field of fields) {
      const column = mapping[field.key];
      const rawValue = column ? raw[column] : undefined;
      const typedValue = column ? rawTypedRows?.[index]?.[column] : undefined;
      const text = cellText(rawValue);

      if (field.key === 'phone') {
        const inspected = inspectPhoneCell(rawValue, typedValue);
        if (!inspected.phone) fail(inspected.error || 'Teléfono no válido');
        values.phone = inspected.phone;
        continue;
      }

      if (field.key === 'age') {
        const parsed = toNumber(text);
        if (text && (parsed === null || parsed < 0 || parsed > 120)) {
          fail('La edad debe estar entre 0 y 120 años');
        }
        values.age = parsed;
        continue;
      }

      if (field.key === 'price') {
        const parsed = toNumber(text);
        if (text && parsed === null) fail('El precio no es válido');
        values.price = parsed;
        continue;
      }

      if (field.key === 'sessions_remaining') {
        const parsed = toNumber(text);
        if (text && parsed === null) fail('Las sesiones restantes no son válidas');
        values.sessions_remaining = parsed;
        continue;
      }

      if (field.key === 'start_date' || field.key === 'end_date') {
        const parsed = parseDate(text);
        if (text && parsed === null) fail(`La fecha "${text}" no es válida`);
        values[field.key] = parsed;
        continue;
      }

      values[field.key] = text;
    }

    // Nombre completo -> se separa en nombres y apellidos
    if (kind === 'clients') {
      if (!values.first_name && !values.last_name && values.full_name) {
        const parts = values.full_name.split(/\s+/).filter(Boolean);
        values.first_name = parts[0] || '';
        values.last_name = parts.slice(1).join(' ') || '';
      }
      if (!values.first_name) fail('Falta el nombre de la clienta');
    }

    // Validaciones de formato
    if (kind === 'clients') {
      if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        fail('El email no es válido');
      }
    }

    if (rowOk) {
      validCount += 1;
    } else {
      const missing = fields
        .filter((f) => f.required && !mapping[f.key])
        .map((f) => f.label);
      issues.push({
        row: index + 1,
        message: missing.length
          ? `Falta mapear la columna: ${missing.join(', ')}`
          : rowError || 'Revisa los datos de la fila',
      });
    }

    rows.push({ index: index + 1, values });
  });

  return { rows, issues, validCount };
};

/** Rango de filas a enviar al servidor (excluye las que tienen errores). */
export const buildPayload = (
  result: ParseResult,
  kind: ImportKind
): Record<string, any>[] =>
  result.rows
    .filter((row) => {
      const issue = result.issues.find((i) => i.row === row.index);
      return !issue;
    })
    .map((row) => {
      const values = row.values;
      if (kind === 'clients') {
        return {
          first_name: values.first_name || null,
          last_name: values.last_name || null,
          email: values.email || null,
          phone: values.phone || null,
          age: values.age ?? null,
          sex: values.sex || null,
          status: values.status || null,
          emergency_contact: values.emergency_contact || null,
          notes: values.notes || null,
        };
      }
      return {
        phone: values.phone || null,
        plan_name: values.plan_name || null,
        start_date: values.start_date || null,
        end_date: values.end_date || null,
        price: values.price ?? null,
        status: values.status || null,
        payment_method: values.payment_method || null,
        payment_status: values.payment_status || null,
        sessions_remaining: values.sessions_remaining ?? null,
      };
    });
